import React, { useState, useRef, useEffect } from 'react';

/**
 * Dropdown component
 * @param {Array} options - Array of { value, label }
 * @param {string} value - Selected value
 * @param {function} onChange - Callback when value changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disable the dropdown
 * @param {string} className - Additional classes for the container
 */
const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const ref = useRef();

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowDown') {
        setHighlighted((h) => (h + 1) % options.length);
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        setHighlighted((h) => (h - 1 + options.length) % options.length);
        e.preventDefault();
      } else if (e.key === 'Enter') {
        if (open && highlighted >= 0 && highlighted < options.length) {
          onChange && onChange(options[highlighted].value);
          setOpen(false);
        }
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, highlighted, options, onChange]);

  // Reset highlight when open
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((opt) => opt.value === value);
      setHighlighted(idx >= 0 ? idx : -1);
    }
  }, [open, value, options]);

  const selected = options.find((opt) => opt.value === value);

  return (
    <div ref={ref} className={`relative w-full min-w-[120px] ${className}`}> 
      <button
        type="button"
        className={`flex items-center justify-between w-full px-4 py-2 rounded-full border bg-white shadow-sm text-left transition focus:ring-2 focus:ring-blue-200 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${open ? 'ring-2 ring-blue-300' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${!selected ? 'text-gray-400' : ''}`}>{selected ? selected.label : placeholder}</span>
        <svg className={`w-4 h-4 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <ul
          className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto animate-fade-in border"
          tabIndex={-1}
          role="listbox"
        >
          {options.length === 0 && (
            <li className="px-4 py-2 text-gray-400 text-sm">No options</li>
          )}
          {options.map((opt, idx) => (
            <li
              key={opt.value}
              className={`px-4 py-2 cursor-pointer transition rounded-xl ${idx === highlighted ? 'bg-blue-100 text-blue-700' : ''} ${opt.value === value ? 'font-bold bg-blue-50' : ''}`}
              onMouseEnter={() => setHighlighted(idx)}
              onMouseDown={() => {
                onChange && onChange(opt.value);
                setOpen(false);
              }}
              role="option"
              aria-selected={opt.value === value}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown; 